import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { cloneDeep, isArray } from 'lodash-es';
import {
  InsuranceQuoteService as _iq,
  IQErrorReason,
  SubmitQuoteError,
} from '@/services/store/insurance-quote.service';
import { QuoteTypeCodes, IQuote, ISubmitQuote, IPolicyHolderDetails } from '@/interfaces';
import { useIQOriginalQuoteStore } from '@/store';
import { JP_GENERIC_CODE, JP_POLICY_PAID_LATER } from '@/constants/jeopardy-codes.const';
import { PaymentTypes } from '@/enums/payment-types.enum';
import { getQuoteHasBeenSubmitted, setQuoteSubmitted } from '@/utils/local-storage';
import { quoteComplete } from '@/utils/insurance/policy-helper';
import { mapPrimaryPolicyholderAddress } from '@/mappers/policyholder.mapper';

export const usePetStore = defineStore('Pet', () => {
  // ------------------------------------------------------------- //
  // Define State
  // ------------------------------------------------------------- //
  const quote = ref<Partial<IQuote>>({});
  const sessionId = ref<string>('');
  const quoteId = ref<string>('');
  const quoteType = computed<string>(() => (quote.value.quoteType && QuoteTypeCodes[quote.value.quoteType]) || '');
  const updatePostalAddressRequired = ref(false);
  const originalQuoteStore = useIQOriginalQuoteStore();
  const jeopardies = ref<IQErrorReason[]>([]);
  const submitQuoteErrors = ref<SubmitQuoteError[]>([]);
  const stepsConfigured = ref(false);
  const emailSendCount = ref(0);
  const suburb = ref<string>('');
  const showAccountLoginInfoBox = ref(false);
  const isResume = ref(false);
  const premiumHasChanged = ref(false);

  const originalQuote = computed(() => {
    return originalQuoteStore.quote;
  });

  const policyStartDateHasPassed = computed(() => {
    if (quote.value.insuranceStartDate?.value) {
      const today = new Date();
      const quoteStartDate = cloneDeep(quote.value.insuranceStartDate.value);
      quoteStartDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);
      return quoteStartDate < today;
    }
    return false;
  });

  const checkPremiumHasChanged = (newQuote: Partial<IQuote>) => {
    premiumHasChanged.value =
      newQuote?.insuranceQuoteSummary?.yearlyAnnualPremium != quote.value.insuranceQuoteSummary?.yearlyAnnualPremium;
  };

  const resetPremiumHasChanged = () => {
    premiumHasChanged.value = false;
  };

  const refreshOriginalQuote = () => {
    originalQuoteStore.setQuote(quote.value, true);
  };

  const fetchQuote = async () => {
    // get quote id
    const params = new URLSearchParams(window.location.search);
    sessionId.value = params.get('ref') ?? '';
    if (!sessionId.value) throw 400;
    const res = await _iq.fetchQuote(sessionId.value);
    quote.value = res?.quote;
    quoteId.value = res?.quote.id;

    originalQuoteStore.setQuote(quote.value);
    if (quote.value.paymentDueDate || quote.value.paymentType == PaymentTypes.PayLater) throw JP_POLICY_PAID_LATER;
    else if (quote.value.policyReference) throw JP_GENERIC_CODE; // throw error if quote already bound
  };

  const getSessionId = () => {
    return _iq.getSessionId(quote.value.id!);
  };

  /**
   * Create new quote
   *
   * @param quote
   */
  const createQuote = async () => {
    try {
      const response = await _iq.createQuote(quote.value);
      if (response.quote) {
        quote.value = response.quote;
        quoteId.value = response.quote.id;
      }
    } catch (error) {
      await handleError(error);
    }
  };

  /**
   * Apply Business Rules
   *
   * @param formData
   */
  const applyBusinessRules = async () => {
    try {
      if (quote?.value?.id && getQuoteHasBeenSubmitted(quote.value.id)) {
        throw JP_GENERIC_CODE;
      }

      const response = await _iq.applyBusinessRules(quoteId.value, quote.value);

      // Sync with the server quote before checking jeopardies so a
      // resubmit (e.g. after JP24) validates against a matching quote (DSQ-2559).
      quote.value = response?.quote || quote.value;

      jeopardies.value = response?.reasonCodes || [];
      if (jeopardies.value.length > 0 && jeopardies.value.some((j) => j.typeOfReason === 0)) {
        throw jeopardies.value.find((j) => j.typeOfReason === 0)?.code;
      }
    } catch (error) {
      await handleError(error);
    }
  };

  /**
   * Save / Update Quote
   *
   * @param formData
   */
  const saveQuote = async () => {
    try {
      const response = await _iq.saveQuote(quoteId.value, quote.value);
      quote.value = response?.quote || quote.value;
    } catch (error) {
      await handleError(error);
    }
  };

  /**
   * Calculate quote price
   *
   * @returns
   */
  const calculatePrice = async () => {
    try {
      const response = await _iq.calculatePrice(quoteId.value, quote.value);

      // Sync the client quote with the server session before evaluating
      // jeopardies so a subsequent ApplyRules/CalculatePrice call validates
      // against a matching quote (see applyBusinessRules for details).
      checkPremiumHasChanged(response?.quote);
      quote.value = response?.quote || quote.value;

      jeopardies.value = response?.reasonCodes || [];
      if (jeopardies.value.length > 0 && jeopardies.value.some((j) => j.typeOfReason === 0)) {
        throw jeopardies.value.find((j) => j.typeOfReason === 0)?.code;
      }
    } catch (error) {
      await handleError(error);
    }
  };

  const applyRulesAndSave = async (calculateQuotePrice: boolean = true) => {
    submitQuoteErrors.value = [];

    // Apply business rules
    await applyBusinessRules();

    if (calculateQuotePrice) {
      // Save Quote for Pricing
      await saveQuote();

      // Recalculate quote price
      await calculatePrice();
    }

    // Save quote with new price
    await saveQuote();
  };

  /**
   * Submit Quote
   *
   * @param formData
   */
  const submitQuote = async (submitQuote: ISubmitQuote) => {
    try {
      submitQuoteErrors.value = [];
      const response = await _iq.submitQuote(quoteId.value, submitQuote);

      quote.value = response?.quote || quote.value;
      submitQuoteErrors.value = response?.errors || [];

      if (!response.success && submitQuoteErrors?.value?.length > 0) {
        //DSQ-2268 INC0216952  if the policy number is generated than don't have to throw an error -- should go to confirmation screen
        const hasPolicyNumberAlreadyGenerated = response.quote.policyReference && response.quote.policyReference != '';
        if (submitQuoteErrors.value[0].errorType && !hasPolicyNumberAlreadyGenerated)
          throw new Error(submitQuoteErrors.value[0].errorDescription);
      }

      if (quoteComplete(response) && quote?.value?.id) {
        setQuoteSubmitted(quote.value.id.toString());
      }

      return response;
    } catch (error) {
      await handleError(error);
    }
  };

  /**
   * Send Quote Email
   *
   * @param formData
   */
  const sendQuoteEmail = async (destinationAddress: string) => {
    await _iq.sendQuoteEmail(quoteId.value, destinationAddress);
  };

  /**
   * DSQ-308 primary policyholder must agree t&c if postal address changed
   */
  const checkUpdatePostalAddressRequired = async (): Promise<boolean> => {
    const partyId = quote.value.primaryPolicyHolderDetails?.partyId?.value;
    const dpid = quote.value.primaryPolicyHolderDetails?.dpid?.value as number;

    if (!dpid && partyId) {
      return true; // show t&c if manual / oversea address, only if it's been party matched.
    } else if (!partyId) {
      return false; // hide t&c for new contact
    }
    const res = await _iq.checkUpdatePostalAddressRequired(partyId, dpid);
    updatePostalAddressRequired.value = res ?? false;
    return res;
  };

  const updatePostalAddress = async (): Promise<boolean> => {
    const partyId = quote.value.primaryPolicyHolderDetails?.partyId?.value;
    if (!partyId) return false;

    const postalAddress = mapPrimaryPolicyholderAddress(
      quote.value?.primaryPolicyHolderDetails as IPolicyHolderDetails
    );

    const res = await _iq.updatePostalAddress(partyId, postalAddress);

    return res;
  };

  const handleError = async (error: any) => {
    const isErrorArray = isArray(error);
    if (!isErrorArray && error?.code) {
      submitQuoteErrors.value = [error.code];
      throw error.code;
    }

    if (isErrorArray && error?.length > 0) {
      submitQuoteErrors.value = error[0]?.code;
    }

    throw error;
  };

  return {
    quote,
    quoteId,
    sessionId,
    quoteType,
    originalQuote,
    policyStartDateHasPassed,
    updatePostalAddressRequired,
    jeopardies,
    emailSendCount,
    suburb,
    showAccountLoginInfoBox,
    isResume,
    checkUpdatePostalAddressRequired,
    updatePostalAddress,
    fetchQuote,
    getSessionId,
    createQuote,
    applyBusinessRules,
    saveQuote,
    calculatePrice,
    applyRulesAndSave,
    submitQuote,
    submitQuoteErrors,
    sendQuoteEmail,
    stepsConfigured,
    refreshOriginalQuote,
    premiumHasChanged,
    resetPremiumHasChanged,
  };
});
